/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Button, Input, Segment } from "semantic-ui-react";
import { TextField } from "@mui/material";
import TextareaAutosize from 'react-textarea-autosize';
import mammoth from "mammoth";
import { globalLoadingOff, globalLoadingOn } from "../slices/globalLoading/globalLoadingSlice";
import { changeBrowserTabTitle } from "../slices/browserTabTitle/browserTabTitleSlice";
import { changeTool } from "../slices/footer/footerSlice";
import { selectCurrentUserApiToken, selectSseConnectionId } from "../slices/currentUser/currentUserSlice";
import { editModeOn } from "../slices/editMode/editModeSlice";
import submitDocxForConversion, { addSectionsToCompositionResource } from "../functions/submitDocxForConverstionAPI";
import updateFhirJson from "../functions/updateFhirJsonAPI";
import { compositionTypeCodeGuideline, compositionTypeCodeRecommendation, sectionCodeDiscussion } from "../dictionaries/CodeSystemLookup";
import ProfilesByResourceType from "../dictionaries/ProfilesByResourceType";
import ProfileSpecificCreateResourcePage from "../resourceComponents/ProfileSpecificCreateResourcePage";

const submitDocx = async (apiToken, docxToHTML, setFhirOutputState, dispatch) => {
    dispatch(globalLoadingOn());
    let response = await submitDocxForConversion(docxToHTML, apiToken);
    if (response.success === false) {
        alert("We were unable to convert this DOCX file. Check the formatting, and let us know if you need us to investigate.");
    } else {
        setFhirOutputState(JSON.stringify(response.sectionsarraystring, null, 2));
    }
    dispatch(globalLoadingOff());
}

const addSectionsToComposition = async (apiToken, sectionArray, foiString, dispatch) => {
    dispatch(globalLoadingOn());
    let jsonSectionArray = JSON.parse(sectionArray);
    if (foiString && typeof foiString === "string" && !isNaN(foiString) && jsonSectionArray.length > 0 && apiToken) {
        let response = await addSectionsToCompositionResource(foiString, jsonSectionArray, apiToken);
        dispatch(globalLoadingOff());
        if (response?.success) {
            if (response.error) {
                alert(response.error);
                return "ERROR";
            }
            if (response.revisedCompositionJson) {
                return response.revisedCompositionJson;
            }
        } else {
            alert("Problem with adding sections to the Composition Resource " + foiString);
            return "ERROR";
        }
    } else {
        alert("Problem adding " + jsonSectionArray.length + " sections to Resource " + foiString);
        return "ERROR";
    }
}

const AddToCompositionResource = ({ apiToken, fhirOutputState,
    addToFoiState, setAddToFoiState }) => {

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const connectionId = useSelector(selectSseConnectionId);

    return <>
        <Button className="formButton positive" content="Add to a Composition Resource" compact
            onClick={async () => {
                dispatch(globalLoadingOn());
                let revisedCompositionJson = await addSectionsToComposition(apiToken, fhirOutputState, addToFoiState, dispatch);
                if (revisedCompositionJson && revisedCompositionJson !== "ERROR") {
                    let compositionProfiles = [];
                    if (revisedCompositionJson.meta?.profile?.includes("http://hl7.org/fhir/uv/ebm/StructureDefinition/recommendation") ||
                        revisedCompositionJson.meta?.profile?.includes("https://hl7.org/fhir/uv/ebm/StructureDefinition/recommendation")) {
                        compositionProfiles.push("recommendationProfile");
                    }
                    if (revisedCompositionJson.meta?.profile?.includes("http://hl7.org/fhir/uv/ebm/StructureDefinition/guideline") ||
                        revisedCompositionJson.meta?.profile?.includes("https://hl7.org/fhir/uv/ebm/StructureDefinition/guideline")) {
                        compositionProfiles.push("guidelineProfile");
                    }
                    if (revisedCompositionJson.meta?.profile?.includes("http://hl7.org/fhir/uv/ebm/StructureDefinition/evidence-report-package") ||
                        revisedCompositionJson.meta?.profile?.includes("https://hl7.org/fhir/uv/ebm/StructureDefinition/evidence-report-package")) {
                        compositionProfiles.push("evidenceReportPackageProfile");
                    }
                    if (revisedCompositionJson.meta?.profile?.includes("http://hl7.org/fhir/uv/ebm/StructureDefinition/m11-report") ||
                        revisedCompositionJson.meta?.profile?.includes("https://hl7.org/fhir/uv/ebm/StructureDefinition/m11-report")) {
                        compositionProfiles.push("m11reportProfile");
                    }
                    if (revisedCompositionJson.meta?.profile?.includes("http://hl7.org/fhir/uv/ebm/StructureDefinition/summary-of-findings") ||
                        revisedCompositionJson.meta?.profile?.includes("https://hl7.org/fhir/uv/ebm/StructureDefinition/summary-of-findings")) {
                        compositionProfiles.push("summaryOfFindingsProfile");
                    }
                    if (revisedCompositionJson.meta?.profile?.includes("http://hl7.org/fhir/uv/ebm/StructureDefinition/comparative-evidence-report") ||
                        revisedCompositionJson.meta?.profile?.includes("https://hl7.org/fhir/uv/ebm/StructureDefinition/comparative-evidence-report")) {
                        compositionProfiles.push("comparativeEvidenceReportProfile");
                    }
                    if (revisedCompositionJson.meta?.profile?.includes("http://hl7.org/fhir/uv/ebm/StructureDefinition/baseline-measure-report") ||
                        revisedCompositionJson.meta?.profile?.includes("https://hl7.org/fhir/uv/ebm/StructureDefinition/baseline-measure-report")) {
                        compositionProfiles.push("baselineMeasureReportProfile");
                    }
                    if (revisedCompositionJson.meta?.profile?.includes("http://hl7.org/fhir/uv/ebm/StructureDefinition/participant-flow-report") ||
                        revisedCompositionJson.meta?.profile?.includes("https://hl7.org/fhir/uv/ebm/StructureDefinition/participant-flow-report")) {
                        compositionProfiles.push("participantFlowReportProfile");
                    }
                    if (revisedCompositionJson.meta?.profile?.includes("http://hl7.org/fhir/uv/ebm/StructureDefinition/outcome-measure-report") ||
                        revisedCompositionJson.meta?.profile?.includes("https://hl7.org/fhir/uv/ebm/StructureDefinition/outcome-measure-report")) {
                        compositionProfiles.push("outcomeMeasureReportProfile");
                    }

                    await updateFhirJson(revisedCompositionJson.id, revisedCompositionJson,
                        revisedCompositionJson.title || revisedCompositionJson.name, apiToken, connectionId);
                    //next step is to display the Resource Builder in edit mode with the revised json TODO confirm it works
                    dispatch(editModeOn());
                    navigate("/" + revisedCompositionJson.id);
                }
                dispatch(globalLoadingOff());
            }}
            disabled={fhirOutputState === "[]" || !addToFoiState} />
        &nbsp;&nbsp;&nbsp;
        <TextField style={{ width: "240px" }} className="inputField" type='text' size="small" variant='outlined'
            label='Composition Resource FOI' value={addToFoiState}
            onChange={(e) => { setAddToFoiState(e.target.value); }} />
    </>
}

const CreateNewGuideline = ({ fhirOutputState }) => {

    let resourceStarter = {
        "resourceType": "Composition",
        "meta": { "profile": ["http://hl7.org/fhir/uv/ebm/StructureDefinition/guideline"] },
        "title": document.title,
        "type": compositionTypeCodeGuideline,
        "section": []
    };
    resourceStarter.section = ProfilesByResourceType.Composition.Guideline.sectionStarter;
    let jsonSectionArray = JSON.parse(fhirOutputState);
    let discussionSectionFound = false;
    if (resourceStarter.section?.length) {
        for (let section of resourceStarter.section) {
            if (section.code?.coding?.[0]?.code === "discussion") {
                discussionSectionFound = true;
                if (!section.section) {
                    section.section = [];
                }
                section.section = section.section.concat(jsonSectionArray);
                break;
            }
        }
        if (!discussionSectionFound) {
            let discussonSection = {
                "title": "Discussion",
                "code": sectionCodeDiscussion,
                "section": jsonSectionArray
            };
            resourceStarter.section.push(discussonSection);
        }
    }
    return <>
        <h2 style={{
            zIndex: "-1", margin: "0px", width: "100%", textAlign: "center",
            top: "8px", position: "fixed", color: "#AC3B61"
        }}>Computable Publishing<sup style={{ verticalAlign: "super", fontSize: "x-small" }}>®</sup>: Create Guideline</h2>
        <ProfileSpecificCreateResourcePage resourceType={"Composition"}
            resourceStarter={resourceStarter} />
    </>
}

const CreateNewRecommendation = ({ fhirOutputState }) => {

    let resourceStarter = {
        "resourceType": "Composition",
        "meta": { "profile": ["http://hl7.org/fhir/uv/ebm/StructureDefinition/recommendation"] },
        "title": document.title,
        "type": compositionTypeCodeRecommendation,
        "section": []
    };
    resourceStarter.section = ProfilesByResourceType.Composition.Recommendation.sectionStarter;
    let jsonSectionArray = JSON.parse(fhirOutputState);
    let discussionSectionFound = false;
    if (resourceStarter.section?.length) {
        for (let section of resourceStarter.section) {
            if (section.code?.coding?.[0]?.code === "discussion") {
                discussionSectionFound = true;
                if (!section.section) {
                    section.section = [];
                }
                section.section = section.section.concat(jsonSectionArray);
                break;
            }
        }
        if (!discussionSectionFound) {
            let discussonSection = {
                "title": "Discussion",
                "code": sectionCodeDiscussion,
                "section": jsonSectionArray
            };
            resourceStarter.section.push(discussonSection);
        }
    }
    return <>
        <h2 style={{
            zIndex: "-1", margin: "0px", width: "100%", textAlign: "center",
            top: "8px", position: "fixed", color: "#AC3B61"
        }}>Computable Publishing<sup style={{ verticalAlign: "super", fontSize: "x-small" }}>®</sup>: Create Recommendation</h2>
        <ProfileSpecificCreateResourcePage resourceType={"Composition"}
            resourceStarter={resourceStarter} />
    </>
}

const AddSectionsToComposition = ({ fhirOutputState, addToFoiState, setAddToFoiState,
    apiToken }) => {
    const [createNewGuidelineState, setCreateNewGuidelineState] = useState(false);
    const [createNewRecommendationState, setCreateNewRecommendationState] = useState(false);

    return <>
        <AddToCompositionResource fhirOutputState={fhirOutputState}
            apiToken={apiToken}
            addToFoiState={addToFoiState} setAddToFoiState={setAddToFoiState} />
        &nbsp;&nbsp;&nbsp;
        <Button className="formButton positive" content="Create New Guideline" compact
            onClick={() => {
                setCreateNewGuidelineState(true);
            }}
            disabled={fhirOutputState === "[]"} />
        {createNewGuidelineState && <CreateNewGuideline fhirOutputState={fhirOutputState} />}
        &nbsp;&nbsp;&nbsp;
        <Button className="formButton positive" content="Create New Recommendation" compact
            onClick={() => {
                setCreateNewRecommendationState(true);
            }}
            disabled={fhirOutputState === "[]"} />
        {createNewRecommendationState && <CreateNewRecommendation fhirOutputState={fhirOutputState} />}
    </>
}

// eslint-disable-next-line no-empty-pattern
const DocxToJsonSectionsConverterPage = ({ }) => {
    const dispatch = useDispatch();

    const apiToken = useSelector(selectCurrentUserApiToken);

    const [docxEntryState, setDocxEntryState] = useState({
        "inputChanged": true,
        "codings": [{ "systemName": "", "systemUri": "", "code": "", "display": "", "qualifiers": [], "preset": "" }],
        text: "",
        docxFile: ""
    });
    const [fhirOutputState, setFhirOutputState] = useState("");
    const [addToFoiState, setAddToFoiState] = useState("");

    useEffect(() => {
        dispatch(changeBrowserTabTitle("FEvIR DOCX-to-JSON-Sections Converter"));
        dispatch(changeTool("Computable Publishing®: DOCX-to-JSON-Sections Converter"));
    }, []);

    return <div>
        <Segment className={`containerSegment maxRemainderOfPageSegment`} raised>
            <br />
            <div>
                <h3>.DOCX file</h3>
                <Input id="inputDocxFile"
                    type="file"
                    accept=".docx"
                    onChange={async (e, data) => {
                        dispatch(globalLoadingOn());
                        try {
                            if (e.target.files.length > 0) {
                                let file = e.target.files[0];
                                await file.arrayBuffer().then((x) => {
                                    mammoth.convertToHtml({ arrayBuffer: x })
                                        .then((result) => {
                                            let html = result.value; // The generated HTML
                                            let messages = result.messages; // Any messages, such as warnings during conversion
                                            //console.log(messages);
                                            setDocxEntryState(prevState => {
                                                return {
                                                    ...prevState,
                                                    inputChanged: true,
                                                    text: "",
                                                    docxToHTML: html
                                                };
                                            });
                                            //console.log(str)
                                        })
                                        .catch(function (error) {
                                            console.error(error);
                                        });
                                });
                            }
                        } catch (e) {
                            alert("File could not be processed.");
                            console.log(e);
                        }
                        dispatch(globalLoadingOff());
                    }}
                />
                &nbsp;&nbsp;&nbsp;
                Mark any section headers as Heading style from the Home menu before submission so they will be recognized as section headers. Make sure the DOCX file is not open.
            </div>
            <br />
            <Button className="formButton positive" content="Convert DOCX to JSON Sections" compact
                onClick={() => {
                    submitDocx(apiToken, docxEntryState.docxToHTML, setFhirOutputState, dispatch);
                }}
                disabled={!docxEntryState.docxToHTML} />
            <br /><br />
            {fhirOutputState &&
                <>
                    <h3>JSON Output</h3>
                    <br />
                    <AddSectionsToComposition fhirOutputState={fhirOutputState}
                        apiToken={apiToken}
                        addToFoiState={addToFoiState} setAddToFoiState={setAddToFoiState} />
                    <br />
                    <TextareaAutosize style={{ width: "98%", color: "#000000" }}
                        spellCheck="false"
                        value={fhirOutputState} />
                    <br />
                    <br />
                    <AddSectionsToComposition fhirOutputState={fhirOutputState}
                        apiToken={apiToken}
                        addToFoiState={addToFoiState} setAddToFoiState={setAddToFoiState} />
                </>
            }
            <br />
        </Segment>
    </div>;
};

export default DocxToJsonSectionsConverterPage;